import React, { useEffect, useState } from "react";
import { getPlan, fmtLimit, effectiveLimit, effectiveTypeLimit, splitShopResources, shopTypes } from "../../../Booking/bookingPlans";
import { fmtHour, slotCfg } from "../../../Booking/bookingService";
import TypeSettingsForm from "../../../Booking/components/TypeSettingsForm";

/**
 * Add / configure shop modal (owner).
 * Owner controls: identity, plan, per-shop resource/type limit overrides,
 * each calendar's slot settings, status, API key rotation.
 */
export default function ShopModal({ shop, plans, resources, resourceTypes, onClose, onSave, onRegenKey, onUpdateType, saving, users }) {
    const isNew = !shop;
    const [accountOwnerId, setAccountOwnerId] = useState(shop?.accountOwnerId || "");
    useEffect(() => {
        setAccountOwnerId(shop?.ownerAccountId || "");
    }, [shop]);


    const [business, setBusiness] = useState(shop?.business || "");
    const [owner, setOwner] = useState(shop?.owner || "");
    const [email, setEmail] = useState(shop?.email || "");
    const [plan, setPlan] = useState(shop?.plan || "free");
    const [resLimit, setResLimit] = useState(
        shop?.resourceLimitOverride === null || shop?.resourceLimitOverride === undefined
            ? "" : String(shop.resourceLimitOverride)
    );
    const [typeLimit, setTypeLimit] = useState(
        shop?.typeLimitOverride === null || shop?.typeLimitOverride === undefined
            ? "" : String(shop.typeLimitOverride)
    );
    const [status, setStatus] = useState(shop?.status || "active");
    const [formError, setFormError] = useState("");
    const [settingsFor, setSettingsFor] = useState(null); // typeId with open settings

    const draft = { ...(shop || {}), plan };
    const effRes = resLimit === ""
        ? (getPlan(plans, plan).resourceLimit ?? Infinity)
        : Math.max(0, parseInt(resLimit, 10) || 0);
    const effTypes = typeLimit === ""
        ? (getPlan(plans, plan).typeLimit ?? Infinity)
        : Math.max(0, parseInt(typeLimit, 10) || 0);
    const split = shop ? splitShopResources({ ...draft, resourceLimitOverride: resLimit === "" ? null : effRes }, resources, plans) : null;
    const mine = shop ? shopTypes(resourceTypes, shop.id) : [];

    const handleSave = () => {
        if (!business.trim() || !owner.trim() || !email.trim()) {
            setFormError("Business, owner and email are required.");
            return;
        }
        setFormError("");
        onSave({
            business: business.trim(),
            owner: owner.trim(),
            email: email.trim(),
            plan,
            ownerAccountId: accountOwnerId,
            resourceLimitOverride: resLimit === "" ? null : Math.max(0, parseInt(resLimit, 10) || 0),
            typeLimitOverride: typeLimit === "" ? null : Math.max(0, parseInt(typeLimit, 10) || 0),
            ...(isNew ? {} : { status }),
        });
    };

    return (
        <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(15,23,42,.45)" }}>
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" role="document">
                <div className="modal-content" style={{ borderRadius: 14, border: "none" }}>
                    <div className="px-6 pt-6 pb-4" style={{ background: "#0f172a", color: "#f8fafc", borderRadius: "14px 14px 0 0" }}>
                        <div className="flex justify-between items-start gap-3">
                            <div>
                                <div className="hk-eyebrow" style={{ color: "#94a3b8" }}>Shop</div>
                                <h5 className="hk-display font-bold text-xl mb-1">
                                    {isNew ? "Onboard new shop" : shop.business}
                                </h5>
                                <p className="text-sm text-[#cbd5e1] mb-0">
                                    {isNew
                                        ? "Shops get an API key instantly and start on the selected plan. They define their own resource types afterwards."
                                        : "Identity, plan, limits and status. Resource types are defined by the shop."}
                                </p>
                            </div>
                            <button type="button" className="btn-close btn-close-white shrink-0" onClick={onClose}></button>
                        </div>
                    </div>
                    <div className="p-6 space-y-5">
                        {formError && <div className="alert alert-danger py-2 mb-0">{formError}</div>}

                        <div>
                            <h6 className="hk-card-title">Identity</h6>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                    <label className="hk-label">Business *</label>
                                    <input className="hk-input" value={business} onChange={(e) => setBusiness(e.target.value)} placeholder="Sunset Bistro" />
                                </div>
                                <div>
                                    <label className="hk-label">Owner *</label>
                                    <input className="hk-input" value={owner} onChange={(e) => setOwner(e.target.value)} placeholder="Liza Mendoza" />
                                </div>
                                <div className="sm:col-span-2">
                                    <label className="hk-label">Contact email *</label>
                                    <input className="hk-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="owner@shop.com" />
                                </div>
                            </div>
                        </div>
                        <div>
                            <h6 className="hk-card-title">Account Owner</h6>
                            <div>
                                <label className="hk-label">Owner Email</label>
                                <select className="hk-select" value={accountOwnerId} onChange={(e) => setAccountOwnerId(e.target.value)}>
                                    <option value="">No Owner</option>
                                    {Object.values(users || {}).map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.name} - ({u.email})
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                        <div>
                            <h6 className="hk-card-title">Plan & limits</h6>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <div>
                                    <label className="hk-label">Plan</label>
                                    <select className="hk-select" value={plan} onChange={(e) => setPlan(e.target.value)}>
                                        {Object.values(plans || {}).map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.label} — {fmtLimit(p.resourceLimit)} res / {fmtLimit(p.typeLimit)} types
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="hk-label">Resource limit (blank = plan)</label>
                                    <input className="hk-input" type="number" min="0" value={resLimit} onChange={(e) => setResLimit(e.target.value)} placeholder={fmtLimit(getPlan(plans, plan).resourceLimit)} />
                                </div>
                                <div>
                                    <label className="hk-label">Type limit (blank = plan)</label>
                                    <input className="hk-input" type="number" min="0" value={typeLimit} onChange={(e) => setTypeLimit(e.target.value)} placeholder={fmtLimit(getPlan(plans, plan).typeLimit)} />
                                </div>
                            </div>
                            <p className="text-xs text-[#64748b] mt-2 mb-0">
                                Effective: <strong className="hk-mono">{fmtLimit(effRes)} resources</strong> ·{" "}
                                <strong className="hk-mono">{fmtLimit(effTypes)} types</strong>.
                                {split && split.locked.length > 0 && (
                                    <span className="text-[#b45309] font-bold">
                                        {" "}Currently {split.locked.length} resource(s) over limit ({split.locked.map((r) => r.name).join(", ")}) — frozen, not deleted.
                                    </span>
                                )}
                            </p>
                        </div>

                        {!isNew && (
                            <div>
                                <h6 className="hk-card-title">Shop's calendars · {mine.length} (slot settings editable)</h6>
                                {mine.length === 0 ? (
                                    <p className="text-sm text-[#64748b] mb-0">No calendars yet — the shop creates them in their console.</p>
                                ) : (
                                    <div className="space-y-2">
                                        {mine.map((t) => {
                                            const cfg = slotCfg(t);
                                            const open = settingsFor === t.id;
                                            return (
                                                <div key={t.id} className="px-4 py-3 rounded-xl border border-[#e2e8f0] bg-white">
                                                    <div className="flex items-center gap-3">
                                                        <span className="flex-1 min-w-0">
                                                            <span className="font-bold text-sm block truncate">{t.name}</span>
                                                            <span className="text-xs text-[#64748b]">
                                                                {t.resourceLabel}s · {cfg.slotMin} min · {fmtHour(cfg.dayStart)}–{fmtHour(cfg.dayEnd % 24)} · {cfg.leadMin}m notice · {cfg.advanceDays}d window
                                                            </span>
                                                        </span>
                                                        <button
                                                            className="hk-btn hk-btn-outline hk-btn-sm shrink-0"
                                                            onClick={() => setSettingsFor(open ? null : t.id)}
                                                        >
                                                            {open ? "Close" : "Settings"}
                                                        </button>
                                                    </div>
                                                    {open && (
                                                        <div className="mt-3">
                                                            <TypeSettingsForm
                                                                type={t}
                                                                onSave={(patch) => {
                                                                    onUpdateType(t.id, patch);
                                                                    setSettingsFor(null);
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {!isNew && (
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <div>
                                    <label className="hk-label">Status</label>
                                    <select className="hk-select" value={status} onChange={(e) => setStatus(e.target.value)}>
                                        <option value="active">Active — accepts bookings</option>
                                        <option value="suspended">Suspended — rejects new bookings</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="hk-label">API key</label>
                                    <div className="flex gap-2">
                                        <input className="hk-input hk-mono" readOnly value={shop.apiKey || ""} style={{ fontSize: "0.75rem" }} />
                                        <button className="hk-btn hk-btn-outline hk-btn-sm shrink-0" onClick={onRegenKey} title="Issue a new key (old one stops working)">
                                            ↻
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-1">
                            <button className="hk-btn hk-btn-outline" onClick={onClose}>Cancel</button>
                            <button className="hk-btn hk-btn-primary" disabled={!!saving} onClick={handleSave}>
                                {saving ? "Saving…" : isNew ? "Onboard shop" : "Save changes"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export { effectiveLimit, effectiveTypeLimit };

